import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@ant-design/icons';
import { Menu, Skeleton } from 'antd';
import classNames from 'classnames';
import type { MenuProps, MenuTheme } from 'antd';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import type { PureSettings } from './typings';
import type {
  MenuDataItem,
  MessageDescriptor,
  Route,
  RouterTypes,
  WithFalse,
} from './typings';
import type { PrivateSiderMenuProps } from './SiderMenu';
import { IconFont } from '@/utils/utils';
import {
  isImg,
  isUrl,
  useMountMergeState,
  getOpenKeysFromMenuData,
} from './utils';
import policyTrack from '@/tracks/policy';
import MenuCounter from './Counter';
import styles from './index.less';

// todo
export type MenuMode =
  | 'vertical'
  | 'vertical-left'
  | 'vertical-right'
  | 'horizontal'
  | 'inline';

export type BaseMenuProps = {
  collapsed?: boolean;
  menuData?: MenuDataItem[];
  mode?: MenuMode;
  onCollapse?: (collapsed: boolean) => void;
  openKeys?: WithFalse<string[]> | undefined;
  handleOpenChange?: (openKeys: string[]) => void;
  iconPrefixes?: string;
  style?: React.CSSProperties;
  theme?: MenuTheme;
  matchMenuKeys?: string[];
  formatMessage?: (message: MessageDescriptor) => string;

  /**
   * @name 处理菜单的 props，可以复写菜单的点击功能，一般结合 Router 框架使用
   * @see 非子级的菜单要使用 subMenuItemRender 来处理
   *
   * @example 使用 a 标签 menuItemRender={(item, defaultDom) => { return <a onClick={()=> history.push(item.path) }>{defaultDom}</a> }}
   * @example 使用 Link 标签 menuItemRender={(item, defaultDom) => { return <Link to={item.path}>{defaultDom}</Link> }}
   */
  menuItemRender?: WithFalse<
    (
      item: MenuDataItem & {
        isUrl: boolean;
        onClick: () => void;
      },
      defaultDom: React.ReactNode,
      menuProps: BaseMenuProps & Partial<PrivateSiderMenuProps>,
    ) => React.ReactNode
  >;
} & Partial<RouterTypes<Route>> &
  Omit<MenuProps, 'openKeys' | 'onOpenChange' | 'title'> &
  Partial<PureSettings>;

// Allow menu.js config icon as string or ReactNode
//   icon: 'setting',
//   icon: 'icon-geren' #For Iconfont ,
//   icon: 'http://demo.com/icon.png',
//   icon: '/favicon.png',
//   icon: <Icon type="setting" />,
const getIcon = (
  icon?: string | React.ReactNode,
  iconPrefixes: string = 'icon-',
): React.ReactNode => {
  if (typeof icon === 'string' && icon !== '') {
    if (isUrl(icon) || isImg(icon)) {
      return <Icon component={() => <img src={icon} alt="icon" />} />;
    }
    if (icon.startsWith(iconPrefixes)) {
      return <IconFont type={icon} />;
    }
  }
  return icon;
};

class MenuUtil {
  constructor(props: BaseMenuProps) {
    this.props = props;
  }

  props: BaseMenuProps;

  getNavMenuItems = (
    menusData: MenuDataItem[] = [],
    isChildren: boolean,
  ): ItemType[] => {
    const navMenuItems = menusData
      .map((item) => this.getSubMenuOrItem(item, isChildren))
      .filter((item) => item);
    return navMenuItems;
  };

  /** Get SubMenu or Item */
  getSubMenuOrItem = (item: MenuDataItem, isChildren: boolean): ItemType => {
    const children = item?.children || item?.routes;
    if (Array.isArray(children) && children.length > 0) {
      const name = this.getIntlName(item);
      const { prefixCls, menu, iconPrefixes } = this.props;
      //  get defaultTitle by menuItemRender
      const defaultTitle = item.icon ? (
        <span className={`${prefixCls}-menu-item`} title={name}>
          {!isChildren && getIcon(item.icon, iconPrefixes)}
          <span className={`${prefixCls}-menu-item-title`}>{name}</span>
        </span>
      ) : (
        <span className={`${prefixCls}-menu-item`} title={name}>
          {name}
        </span>
      );

      // subMenu only title render
      const title = defaultTitle;

      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: menu?.type === 'group' ? ('group' as const) : (undefined as any),
        label: title,
        children: this.getNavMenuItems(children, true),
        onTitleClick: item.onTitleClick,
        key: item.key || item.path,
      } as ItemType;
    }

    return {
      label: this.getMenuItemPath(item, isChildren),
      title: this.getIntlName(item),
      key: item.key! || item.path!,
      disabled: item.disabled,
      onClick: (e) => {
        if (isUrl(item?.path)) {
          window.open(item.path, '_blank');
        }
        item.onTitleClick?.(e);
      },
    };
  };

  getIntlName = (item: MenuDataItem) => {
    const { name, locale } = item;
    const { menu, formatMessage } = this.props;
    if (locale && menu?.locale !== false) {
      return formatMessage?.({
        id: locale,
        defaultMessage: name,
      });
    }
    return name;
  };

  /**
   * 判断是否是http链接.返回 Link 或 a Judge whether it is http link.return a or Link
   *
   * @memberof SiderMenu
   */
  getMenuItemPath = (
    item: MenuDataItem,
    isChildren: boolean,
  ): React.ReactNode => {
    const itemPath = this.conversionPath(item.path || '/');
    const {
      location = { pathname: '/' },
      onCollapse,
      menuItemRender,
      iconPrefixes,
    } = this.props;
    // if local is true formatMessage all name。
    const name = this.getIntlName(item);
    const { prefixCls } = this.props;
    const icon = isChildren ? null : getIcon(item.icon, iconPrefixes);
    const isHttpUrl = isUrl(itemPath);
    const defaultItem = (
      <span
        className={classNames(`${prefixCls}-menu-item`, {
          [`${prefixCls}-menu-item-link`]: isHttpUrl,
        }, item?.customClassName ?? '')}
      >
        {icon}
        <span className={`${prefixCls}-menu-item-title`}>{name}</span>
      </span>
    );

    if (menuItemRender) {
      const renderItemProps = {
        ...item,
        isUrl: isHttpUrl,
        itemPath,
        replace: itemPath === location.pathname,
        onClick: () => {
          if (isHttpUrl) window.open(itemPath, '_blank');
          if (onCollapse) onCollapse(true);
        },
        children: undefined,
      };
      return menuItemRender(renderItemProps, defaultItem, this.props);
    }
    return defaultItem;
  };

  conversionPath = (path: string) => {
    if (path && path.indexOf('http') === 0) {
      return path;
    }
    return `/${path || ''}`.replace(/\/+/g, '/');
  };
}

/**
 * 生成openKeys 的对象，因为设置了openKeys 就会变成受控，所以需要一个空对象
 *
 * @param BaseMenuProps
 */
const getOpenKeysProps = (
  openKeys: React.ReactText[] | false,
  { layout, collapsed }: BaseMenuProps,
): {
  openKeys?: undefined | string[];
} => {
  let openKeysProps = {};
  if (openKeys && !collapsed && ['side', 'mix'].includes(layout || 'mix')) {
    openKeysProps = {
      openKeys,
    };
  }
  return openKeysProps;
};

const BaseMenu: React.FC<BaseMenuProps & PrivateSiderMenuProps> = (props) => {
  const {
    theme,
    mode,
    handleOpenChange,
    style,
    menuData,
    menu,
    matchMenuKeys,
    collapsed,
    selectedKeys: propsSelectedKeys,
    onSelect,
    openKeys: propsOpenKeys,
  } = props;

  // 用于减少 defaultOpenKeys 计算的组件
  const defaultOpenKeysRef = useRef<string[]>([]);

  const { flatMenuKeys } = MenuCounter.useContainer();
  const [defaultOpenAll, setDefaultOpenAll] = useMountMergeState(
    menu?.defaultOpenAll,
  );

  const [openKeys, setOpenKeys] = useMountMergeState<WithFalse<React.Key[]>>(
    () => {
      if (menu?.defaultOpenAll) {
        return getOpenKeysFromMenuData(menuData) || [];
      }
      if (propsOpenKeys === false) {
        return false;
      }
      return [];
    },
    {
      value: propsOpenKeys === false ? undefined : propsOpenKeys,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange: handleOpenChange as any,
    },
  );

  const [selectedKeys, setSelectedKeys] = useMountMergeState<
    string[] | undefined
  >([], {
    value: propsSelectedKeys,
    onChange: onSelect
      ? (keys) => {
        if (onSelect && keys) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSelect(keys as any);
        }
      }
      : undefined,
  });

  useEffect(() => {
    if (
      menu?.defaultOpenAll ||
      propsOpenKeys === false ||
      flatMenuKeys.length
    ) {
      return;
    }
    if (matchMenuKeys) {
      setOpenKeys(matchMenuKeys);
      setSelectedKeys(matchMenuKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchMenuKeys.join('-')]);

  useEffect(() => {
    // if pathname can't match, use the nearest parent's key
    if (matchMenuKeys.join('-') !== (selectedKeys || []).join('-')) {
      setSelectedKeys(matchMenuKeys);
    }
    if (
      !defaultOpenAll &&
      propsOpenKeys !== false &&
      matchMenuKeys.join('-') !== (openKeys || []).join('-')
    ) {
      let newKeys: React.Key[] = matchMenuKeys;
      // 如果不自动关闭，则需要把 openKeys 放进去
      if (menu?.autoClose === false) {
        newKeys = Array.from(new Set([...matchMenuKeys, ...(openKeys || [])]));
      }
      setOpenKeys(newKeys);
    } else if (menu?.ignoreFlatMenu && defaultOpenAll) {
      // 忽略用户手动折叠过的菜单状态，折叠按钮切换之后也可实现默认展开所有菜单
      setOpenKeys(getOpenKeysFromMenuData(menuData));
    } else if (flatMenuKeys.length > 0) setDefaultOpenAll(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchMenuKeys.join('-'), collapsed]);

  const openKeysProps = useMemo(
    () => getOpenKeysProps(openKeys, props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openKeys && openKeys.join(','), props.layout, props.collapsed],
  );

  const [menuUtils] = useState(() => new MenuUtil(props));

  if (menu?.loading) {
    return (
      <div
        style={
          mode?.includes('inline')
            ? { padding: 24 }
            : {
              marginTop: 16,
            }
        }
      >
        <Skeleton
          active
          title={false}
          paragraph={{
            rows: mode?.includes('inline') ? 6 : 1,
          }}
        />
      </div>
    );
  }

  // sync props
  menuUtils.props = props;

  // 这次 openKeys === false 的时候的情况，这种情况下帮用户选中一次
  // 第二此不会使用，所以用了 defaultOpenKeys
  // 这里返回 null，是为了让 defaultOpenKeys 生效
  if (props.openKeys === false && !props.handleOpenChange) {
    defaultOpenKeysRef.current = matchMenuKeys;
  }

  const finallyData = menuData;

  if (finallyData && finallyData?.length < 1) {
    return null;
  }
  return (
    <Menu
      {...openKeysProps}
      key="Menu"
      mode={mode}
      items={menuUtils.getNavMenuItems(finallyData, false)}
      inlineIndent={16}
      defaultOpenKeys={defaultOpenKeysRef.current}
      theme={theme}
      selectedKeys={selectedKeys}
      style={style}
      className={classNames(styles.menu, {
        [styles['menu-collspsed']]: collapsed,
      })}
      rootClassName={styles['custom-menu-root']}
      onOpenChange={setOpenKeys}
      onClick={(info) => {
        /** 资产监控菜单埋点 */
        if (info.key === '/policy') {
          policyTrack.policyMenu();
        }
      }}
    />
  );
};

export default BaseMenu;
